import {catchError, debounceTime, first} from 'rxjs/operators';
import {Component, OnDestroy, OnInit, ChangeDetectorRef, signal, viewChild, inject} from '@angular/core';
import {CheckInService} from "../../services/check-in.service";
import {MatDialog} from "@angular/material/dialog";
import {FilterComponent} from "../filter/filter.component";
import {UntypedFormControl} from "@angular/forms";
import { of, Subscription } from "rxjs";
import {StatusesService} from "../../services/statuses.service";
import { HttpResponse } from "@angular/common/http";
import {PaginatedItems, PaginatedOrders} from "../../models/types";
import {uniq} from "lodash-es";
import * as XLSX from "xlsx";
import {OrderDateRangeComponent} from "../order-date-range/order-date-range.component";
import {DatePipe} from "@angular/common";
import {RoleService} from "../../role/role.service";
import { AdminService } from '../../admin/services/admin.service';
import {MatTableDataSource} from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CheckIn } from '../../models/checkin.model';
import { ChatService } from '../../chat/chat.service';

@Component({
    selector: 'app-orders',
    templateUrl: './orders.component.html',
    styleUrls: ['./orders.component.scss'],
    standalone: false
})
export class OrdersComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['correlative-id', 'requirement-name', 'object-identifier', 'client-name', 'check_in_time', 'status_id', 'actions'];

  dataSource: MatTableDataSource<any>;

  statuses: any[] = [];

  orders: any[] = undefined;

  searchControl: UntypedFormControl = new UntypedFormControl();

  filter: any = this.getFilter();

  page = 1;

  total_pages = 1;

  downloading = signal(false);

  filters: any = [{
    name: 'Estado',
    key: 'status_id',
    options: [],
    multi: true,
  },
  ];

  subs: Subscription[] = [];

  public working = signal(true);

  private search: any;

  count = 0;

  loadingMore = signal(false);

  public sort = 'correlative_id desc';
  
  totalByStatus: any = {};


  private readonly snackBar = inject(MatSnackBar);

  private readonly chatService = inject(ChatService);

  constructor(
    private orderService: CheckInService,
    private matDialog: MatDialog,
    private statusesService: StatusesService,
    public roleService: RoleService,
    public appService: AdminService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(search?) {
    this.appService.pageTitle.set(this.appService.getText('order_plural'));
    this.ngOnDestroy();
    this.subs.push(this.searchControl.valueChanges.pipe(debounceTime(500)).subscribe((value) => {
      if (this.statuses.length > 0) {
        this.search = value;
        this.getOrders();
      }
    }));
    this.subs.push(this.appService.locations$.subscribe((value) => {
      if (this.statuses.length > 0) {
        this.getOrders();
      }
    }));
    this.subs.push(this.statusesService.statuses('Status::Order').subscribe((value) => {
      this.statuses = value;
      if (!this.filter && value.length > 0) {
        this.filter = this.statuses.filter((status) => !status.finish).map((s) => s.id);
        this.saveFilter();
      }
      if (this.statuses.length > 0) {
        this.getOrders();
      }
    }));
    this.getMore();
    this.dataSource = new MatTableDataSource(this.orders);
  }

  setTotalsByStatus(totals) {
    this.totalByStatus = totals.reduce((acc, current) => {
      acc[current.status_id] = current;
      return acc;
    }, {});
  }

  statusOn(id: number) {
    if (this.filter) {
      const index = this.filter.findIndex((s) => parseInt(s, 0) === id);
      return index >= 0;
    }
    return false;
  }

  getStatusCompletion(status_id: number, order) {
    const index = order.status_logs.findIndex((statusLog) => statusLog.status_id === status_id);
    if (index >= 0) {
      return order.status_logs[index].completed;
    }
    return false;
  }

  getOrders(loadingMore?) {
    if (!loadingMore) {
      this.resetOrders();
      this.working.set(true);
    }
    this.orderService.all({ status_id: this.filter, page: this.page }, this.search, this.sort)
      .pipe(
        catchError(() => {
          this.working.set(false);
          this.loadingMore.set(false);
          this.snackBar.open("Error al cargar las órdenes", "Cerrar", {
            duration: 3000,
            panelClass: ['snackbar-error'],
          });
          return of({ body: { orders: [], totals_by_status: [], total: 0, page: 1, total_pages: 1 } });
        }),
      )
      .subscribe((response:HttpResponse<PaginatedOrders>) => {
        this.cdr.markForCheck();
        if(loadingMore){
          this.orders = (this.orders || []).concat(response.body.orders);
        }else{
          this.orders = response.body.orders;
        }
        this.setTotalsByStatus(response.body.totals_by_status);
        this.count = response.body.total;
        this.page = response.body.page;
        this.total_pages = response.body.total_pages;
        this.working.set(false);
        this.loadingMore.set(false);
        this.dataSource.data = this.orders;
        this.appService.pageTitle.set(this.appService.getText('order_plural'));//+ " (" + this.count + ")"
    });
  }

  resetOrders(){
    this.cdr.markForCheck();
    const element = document.getElementById('orders');
    if (element) {
      element.scrollTop = 0;
    }
    this.page = 1;
    this.total_pages = 1;
    this.count = 0;
  }

  getMore() {
    const element = document.getElementById('orders');
    if (element) {
      this.loadingMore.set(true);
      this.page++;
      this.getOrders(true);
    }
  }

  getFilter() {
    const value = localStorage.getItem('orders_filter');
    let filter = null;
    if (value) {
      filter = uniq(value.split(',')).map((v) => parseInt(v, 0));
    }
    return filter;
  }

  saveFilter() {
    this.filter.push(0);
    this.filter = uniq(this.filter);
    localStorage.setItem('orders_filter', this.filter);
  }

  toggleFilter(id: number) {
    const index = this.filter.findIndex((s) => s === id);
    if (index >= 0) {
      this.filter.splice(index, 1);
    } else {
      this.filter.push(id);
    }
    this.saveFilter();
    this.getOrders();
  }

  openFilters() {
    // Setup Options
    this.filters[0].options = this.statuses.map((s) => [s.id, s.name, this.statusOn(s.id)]);
    this.matDialog.open(FilterComponent, { data: this.filters }).afterClosed().pipe(first()).forEach((result) => {
      if (result) {
        this.filter = result.status_id;
        this.saveFilter();
        this.getOrders();
      }
    });
  }

  openDateRange() {
    this.matDialog.open(OrderDateRangeComponent).afterClosed().pipe(first()).forEach((result) => {
      if (result) {
        this.exportAsExcelFile(result);
      }
    });
  }

  exportAsExcelFile(dateRange?): void {
    this.downloading.set(true);
    this.orderService.allPost({ status_id: this.filter, page: -1, dates: dateRange }, this.search)
      .subscribe((value:HttpResponse<PaginatedItems>) => {
        const excelFileName = 'Ordenes de Trabajo';
        const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.formatJson(value.body.items));
        const workbook: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
        const today = new Date();
        const date = `${today.getFullYear()}${today.getMonth() + 1}${today.getDate()}`;
        const time = `${today.getHours()}${today.getMinutes()}${today.getSeconds()}`;
        const name = excelFileName + date + time;
        XLSX.writeFile(workbook, `${name}.xlsx`);
        this.downloading.set(false);
      });
  }

  formatJson(data) {
    if (data) {
      return data.map((row) => {
        const formattedData = {
          ID: row.correlative_id,
          Cliente: row.client_name,
          Rut: row.client_rut,
          'E-Mail': row.client_email,
          Telefono: row.client_phone,
          Recepcionista: row.user_name,
          Marca: row.brand_name,
          Modelo: row.brand_model_name,
          Patente: row.identifier,
          Chasis: row.chasis,
          KMS: row.kms,
          Requerimiento: row.requirement_name,
          Estado: row.status_name,
          'Fecha Recepción': new DatePipe('es-CL').transform(row.check_in_time, 'short'),
          'Fecha Entrega': new DatePipe('es-CL').transform(row.ended_at, 'short'),
          Comentario: row.comment,
        };
        Object.keys(row).filter((value:string) => value.includes('$$') && !value.includes('image')).forEach((value) => {
          const key = value.split('$$')[0].replace(/\'/g, '');
          formattedData[key] = row[value];
        });
        return formattedData;
      });
    }
    return [];
  }

  onSortChange(event: any) {
    if (!event?.direction) {
      this.sort = 'check_in_time desc';
      return;
    }

    this.sort = `${event.active} ${event.direction}`;
    this.getOrders();
  }

  clearSearch() {
    this.searchControl.setValue('');
  }

  getThumbUrl(image:any) {
    const search = 'images%2F';
    let url;
    if (image.url || image.new_url) {
      if (image.video) {
        url = (image.new_url || image.url).replace(search, `${search}thumb@256_`);
        url = url.replace('.mp4', '.mp4.gif');
        url = url.replace('.webm', '.webm.gif');
      } else {
        url = (image.new_url || image.url).replace(search, `${search}thumb@256_`);
      }
    }
    return url;
  }

  openChat(event: MouseEvent, order: CheckIn) {
    event.stopPropagation();

    this.chatService.addChat("Order", order.id).pipe(first()).subscribe();
  }

  ngOnDestroy() {
    this.subs.forEach((s) => s.unsubscribe());
  }
}
